import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Modal,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { saveAssessmentTest } from "../../services/TrainingServices";
import CommonDivider from "../../../common/components/divider/CommonDivider";
import { CloseModalIcon } from "../../../common/components/assets/commonassets/CommonAssets";
import ConfirmationModal from "../../../common/components/ConfirmationModal";
import { errorAlert } from "../../../common/components/Toasts/Toasts";
import { successAlert } from "../../../common/components/Toasts/CustomToasts";

const getModalStyle = (isDarkMode) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "45%",
  bgcolor: isDarkMode ? "#0f172a" : "background.paper",
  border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.1)" : "gray"}`,
  borderRadius: 6,
  boxShadow: isDarkMode ? "0 0 40px rgba(0,0,0,0.6)" : 20,
  p: 2,
});

const CommonAssessmentTestModal = ({
  open,
  onClose,
  backendData = [],
  populateTable,
  selectedDataForAssessment,
  isDarkMode, // ← only new prop
}) => {
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [openCancelConfirm, setOpenCancelConfirm] = useState(false);
  const [openSubmitConfirm, setOpenSubmitConfirm] = useState(false);
  const { control, handleSubmit, watch, setValue, reset } = useForm({
    shouldUnregister: false,
  });
  const [openAssessmentResultModal, setOpenAssessmentResultModal] =
    useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);
  const [resultData, setResultData] = useState({
    passed: true,
    score: 78,
    total: 10,
    correct: 8,
    visible: true,
  });

  useEffect(() => {
    if (!backendData?.length || questions.length) return;

    const mapped = backendData.map((item, index) => ({
      id: item.id,
      name: `q${index + 1}`,
      question: item.question,
      optionTypeId: item.optionTypeId,
      options:
        item.options?.map((opt) => ({ id: opt.id, label: opt.option })) ?? [],
    }));

    setQuestions(mapped);
    setCurrentQuestion(0);

    reset(
      backendData.reduce((acc, q, index) => {
        acc[`q${index + 1}`] = q?.optionTypeId === 3 ? [] : "";
        return acc;
      }, {}),
    );
  }, [backendData, reset]);

  const current = questions[currentQuestion];
  const currentAnswer = watch(current?.name);
  const isAnswered = Array.isArray(currentAnswer)
    ? currentAnswer.length > 0
    : Boolean(currentAnswer);

  const onSubmit = useCallback(
    (data) => {
      const unanswered = questions?.some((q) => {
        const val = data[q?.name];
        return Array.isArray(val) ? val.length === 0 : !val;
      });

      if (unanswered) {
        errorAlert("Please answer all questions before submitting.");
        return;
      }

      setPendingSubmitData(data);
      setOpenSubmitConfirm(true);
    },
    [questions],
  );

  const handleFinalSubmit = () => {
    if (!pendingSubmitData || !selectedDataForAssessment) return;

    const payload = {
      employee: selectedDataForAssessment?.employee_id
        ? { id: selectedDataForAssessment?.employee_id }
        : null,
      assessment:
        (selectedDataForAssessment?.ActiveStatus === "pre" && {
          id: selectedDataForAssessment?.pre_assessment_id,
        }) ||
        (selectedDataForAssessment?.ActiveStatus === "post" && {
          id: selectedDataForAssessment?.post_assessment_id,
        }) ||
        null,
      assessmentType:
        (selectedDataForAssessment?.ActiveStatus === "pre" && {
          id: selectedDataForAssessment?.pre_assessment_type_id,
        }) ||
        (selectedDataForAssessment?.ActiveStatus === "post" && {
          id: selectedDataForAssessment?.post_assessment_type_id,
        }) ||
        null,
      trainingSchedule: selectedDataForAssessment?.trainingscheduleid
        ? { id: selectedDataForAssessment?.trainingscheduleid }
        : null,
      training: selectedDataForAssessment?.trainingid
        ? { id: selectedDataForAssessment?.trainingid }
        : null,
      question: questions.map((q) => {
        const rawAnswer = pendingSubmitData[q?.name];
        let answers = [];

        if (q?.optionTypeId === 1) {
          answers = [{ freeText: rawAnswer, questionAnswer: null }];
        } else if (q?.optionTypeId === 2) {
          answers = [
            { questionAnswer: { id: Number(rawAnswer) }, freeText: null },
          ];
        } else if (q?.optionTypeId === 3) {
          answers = (rawAnswer ?? []).map((id) => ({
            questionAnswer: { id: Number(id) },
            freeText: null,
          }));
        }

        return {
          assessmentDetails: { id: q?.id },
          optionType: { id: q?.optionTypeId },
          answers,
        };
      }),
    };

    saveAssessmentTest(payload)
      .then((response) => response.data)
      .then((res) => {
        if (res.statusCode === 201 || res.statusCode === 200) {
          successAlert(res.message);
          populateTable();
          setOpenSubmitConfirm(false);
          setPendingSubmitData(null);
          setOpenAssessmentResultModal(true);
          onClose(false);
        }
      })
      .catch((error) => {
        errorAlert(error.message);
        setOpenSubmitConfirm(false);
        setPendingSubmitData(null);
      });

    // console.log("Final Payload:", JSON.stringify(payload, null, 2));

    setOpenSubmitConfirm(false);
    setPendingSubmitData(null);
    onClose(false);
  };

  if (!questions?.length) return null;

  const isFirst = currentQuestion === 0;
  const isLast = currentQuestion === questions?.length - 1;
  const progress = Math.round(
    ((currentQuestion + 1) / questions?.length) * 100,
  );

  return (
    <Modal open={open}>
      <Box sx={getModalStyle(isDarkMode)}>
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="flex gap-6 items-center">
            <h1
              className={`font-semibold text-[18px] ${
                isDarkMode ? "text-slate-100" : "text-gray-900"
              }`}
            >
              {(selectedDataForAssessment?.ActiveStatus === "pre" &&
                "Pre-Assessment") ||
                (selectedDataForAssessment?.ActiveStatus === "post" &&
                  "Post-Assessment") ||
                "Assessment"}
            </h1>
            <p
              className={`inline-flex items-center px-3 py-1 text-sm font-semibold rounded-full
              ${
                isDarkMode
                  ? "text-blue-400 bg-blue-900/30 border border-blue-700"
                  : "text-blue-700 bg-blue-50 border border-blue-200"
              }`}
            >
              {currentQuestion + 1} of {questions?.length}
            </p>
          </div>
          <CloseModalIcon onClick={() => setOpenCancelConfirm(true)} />
        </div>

        <CommonDivider />

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div>
            <h2
              className={`text-[17px] font-semibold mt-3 ${
                isDarkMode ? "text-slate-100" : "text-gray-900"
              }`}
            >
              {currentQuestion + 1} . {current?.question}
            </h2>

            <div className="min-h-[200px]">
              {/* Free Text */}
              {current?.optionTypeId === 1 && (
                <Controller
                  key={current?.name}
                  name={current?.name}
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      minRows={3}
                      placeholder="Type your answer here…"
                      variant="outlined"
                      sx={{
                        mt: 2,
                        "& .MuiOutlinedInput-root": {
                          color: isDarkMode ? "#f1f5f9" : "inherit",
                          backgroundColor: isDarkMode
                            ? "rgba(255,255,255,0.05)"
                            : "transparent",
                          "& fieldset": {
                            borderColor: isDarkMode
                              ? "rgba(255,255,255,0.15)"
                              : "",
                          },
                          "&:hover fieldset": {
                            borderColor: isDarkMode
                              ? "rgba(255,255,255,0.3)"
                              : "",
                          },
                        },
                        "& .MuiInputBase-input::placeholder": {
                          color: isDarkMode ? "#475569" : "",
                        },
                      }}
                    />
                  )}
                />
              )}

              {/* Radio */}
              {current?.optionTypeId === 2 && (
                <Controller
                  key={current?.name}
                  name={current?.name}
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <RadioGroup {...field} className="mt-3 gap-1">
                      {current?.options.map((opt) => {
                        const isSelected = field.value === String(opt.id);
                        return (
                          <FormControlLabel
                            key={opt.id}
                            value={String(opt.id)}
                            control={
                              <Radio
                                size="small"
                                sx={{
                                  color: isDarkMode ? "#475569" : "",
                                  "&.Mui-checked": { color: "#3b82f6" },
                                }}
                              />
                            }
                            label={
                              <span
                                className={
                                  isDarkMode
                                    ? "text-slate-300"
                                    : "text-gray-700"
                                }
                              >
                                {opt.label}
                              </span>
                            }
                            sx={{
                              border: "1px solid",
                              borderColor: isSelected
                                ? "#3b82f6"
                                : isDarkMode
                                  ? "rgba(255,255,255,0.1)"
                                  : "#e5e7eb",
                              borderRadius: 2,
                              px: 1.5,
                              py: 0.5,
                              mx: 0,
                              bgcolor: isSelected
                                ? isDarkMode
                                  ? "rgba(59,130,246,0.15)"
                                  : "#eff6ff"
                                : isDarkMode
                                  ? "rgba(255,255,255,0.03)"
                                  : "transparent",
                              transition: "all 0.15s",
                            }}
                          />
                        );
                      })}
                    </RadioGroup>
                  )}
                />
              )}

              {/* Checkbox */}
              {current?.optionTypeId === 3 && (
                <Controller
                  key={current?.name}
                  name={current?.name}
                  control={control}
                  rules={{ validate: (v) => v?.length > 0 }}
                  render={({ field }) => {
                    const selected = field.value ?? [];
                    const toggle = (id) => {
                      const strId = String(id);
                      field.onChange(
                        selected.includes(strId)
                          ? selected.filter((v) => v !== strId)
                          : [...selected, strId],
                      );
                    };
                    return (
                      <FormGroup className="mt-3 gap-1">
                        {current?.options.map((opt) => {
                          const checked = selected.includes(String(opt.id));
                          return (
                            <FormControlLabel
                              key={opt.id}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={checked}
                                  onChange={() => toggle(opt.id)}
                                  sx={{
                                    color: isDarkMode ? "#475569" : "",
                                    "&.Mui-checked": { color: "#3b82f6" },
                                  }}
                                />
                              }
                              label={
                                <span
                                  className={
                                    isDarkMode
                                      ? "text-slate-300"
                                      : "text-gray-700"
                                  }
                                >
                                  {opt.label}
                                </span>
                              }
                              sx={{
                                border: "1px solid",
                                borderColor: checked
                                  ? "#3b82f6"
                                  : isDarkMode
                                    ? "rgba(255,255,255,0.1)"
                                    : "#e5e7eb",
                                borderRadius: 2,
                                px: 1.5,
                                py: 0.5,
                                mx: 0,
                                bgcolor: checked
                                  ? isDarkMode
                                    ? "rgba(59,130,246,0.15)"
                                    : "#eff6ff"
                                  : isDarkMode
                                    ? "rgba(255,255,255,0.03)"
                                    : "transparent",
                                transition: "all 0.15s",
                              }}
                            />
                          );
                        })}
                      </FormGroup>
                    );
                  }}
                />
              )}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center pt-6">
            <button
              type="button"
              disabled={isFirst}
              onClick={() => setCurrentQuestion((p) => p - 1)}
              className={`group flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium border transition-all duration-200 ease-out
                ${
                  isFirst
                    ? isDarkMode
                      ? "border-slate-700 text-slate-600 cursor-not-allowed"
                      : "border-gray-200 text-gray-300 cursor-not-allowed"
                    : isDarkMode
                      ? "border-slate-600 text-slate-300 hover:border-slate-400 hover:text-white hover:bg-slate-700 active:scale-95"
                      : "border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-900 hover:bg-gray-50 active:scale-95"
                }`}
            >
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${
                  !isFirst ? "group-hover:-translate-x-0.5" : ""
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Previous
            </button>

            {!isLast ? (
              <button
                type="button"
                disabled={!isAnswered}
                onClick={() => setCurrentQuestion((p) => p + 1)}
                className={`group flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ease-out
                  ${
                    !isAnswered
                      ? isDarkMode
                        ? "bg-slate-800 text-slate-600 cursor-not-allowed"
                        : "bg-gray-100 text-gray-300 cursor-not-allowed"
                      : "bg-customBlue text-white hover:bg-gray-700 shadow-sm hover:shadow-md active:scale-95"
                  }`}
              >
                Next
                <svg
                  className={`w-4 h-4 transition-transform duration-200 ${
                    isAnswered ? "group-hover:translate-x-0.5" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            ) : (
              <button
                type="submit"
                disabled={!isAnswered}
                className={`group flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ease-out
                  ${
                    !isAnswered
                      ? isDarkMode
                        ? "bg-slate-800 text-slate-600 cursor-not-allowed"
                        : "bg-gray-100 text-gray-300 cursor-not-allowed"
                      : "bg-emerald-500 text-white shadow-sm hover:bg-emerald-600 hover:shadow-md active:scale-95"
                  }`}
              >
                <svg
                  className="w-4 h-4 transition-transform duration-200 group-hover:scale-110"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Submit
              </button>
            )}
          </div>
        </form>

        {/* Commented out exactly as your original */}
        {/* {openAssessmentResultModal && (
          <AssessmentResult
            key={resultData.score}
            passed={resultData.passed}
            score={resultData.score}
            total={resultData.total}
            correct={resultData.correct}
            onRetry={() =>
              setResultData((prev) => ({ ...prev, visible: false }))
            }
          />
        )} */}

        {openCancelConfirm && (
          <ConfirmationModal
            confirmationOpen={openCancelConfirm}
            confirmationHandleClose={() => setOpenCancelConfirm(false)}
            confirmationLabel="Cancel Assessment"
            confirmationMsg="Are you sure you want to cancel the assessment?"
            confirmationButtonMsg="Yes"
            confirmationSubmitFunc={() => {
              successAlert("Your test has been cancelled successfully.");
              setOpenCancelConfirm(false);
              reset();
              setCurrentQuestion(0);
              setQuestions([]);
              onClose(false);
            }}
          />
        )}

        {openSubmitConfirm && (
          <ConfirmationModal
            confirmationOpen={openSubmitConfirm}
            confirmationHandleClose={() => {
              setOpenSubmitConfirm(false);
              setPendingSubmitData(null);
            }}
            confirmationLabel="Submit Assessment"
            confirmationMsg="Are you sure you want to submit the assessment?"
            confirmationButtonMsg="Yes, Submit"
            confirmationSubmitFunc={handleFinalSubmit}
          />
        )}
      </Box>
    </Modal>
  );
};

export default CommonAssessmentTestModal;